
import { defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import { EventBus } from "../../eventBus";
import { Grid } from "@progress/kendo-vue-grid";
import { filterBy, orderBy } from "@progress/kendo-data-query";
import { WorkflowState } from "@/models/workflow-state";
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import { Input } from "@progress/kendo-vue-inputs";
import { Button } from "@progress/kendo-vue-buttons";
import { Dialog } from "@progress/kendo-vue-dialogs";
import { AlertType } from "@/models/alert-type";
import { ICase } from "@/models/case";
import { RootMutations } from "@/store/mutations";
import { mapActions, mapGetters } from "vuex";
import { RootGetters } from "@/store/getters";
import { RootActions } from "@/store/actions";
import ILabel from "@/components/label/ILabel.vue";
import { ISlide } from "../../models/slide";
import { BFF_BASE_PATH } from "@/core/utils";
import { Likelihood } from "@/models/labels";
import IDialog from "@/components/dialog/IDialog.vue";
import { modalTypes } from "@/components/dialog/modalTypes";
import IbIcons from "@/components/icons/ib-icons.vue";

export default defineComponent({
  components: {
    Grid: Grid,
    dropdownlist: DropDownList,
    "k-input": Input,
    "k-button": Button,
    "k-dialog": Dialog,
    ILabel,
    IDialog,
    IbIcons,
  },
  setup() {
    const { t } = useI18n({
      inheritLocale: true,
      useScope: "local",
    });
    return { t };
  },
  data() {
    return {
      showPreferences: false as boolean,
      errorModal: false,
      filter: {
        logic: "or",
        filters: [],
      },
      dataItems: [] as ICase[],
      skip: 0 as number,
      take: this.$store.state.config["cases_list_page_size"] as number,
      sort: [{ field: "date", dir: "desc" }],
      selectedID: "" as string,
      selectedField: "selected" as string,
      AlertType,
      modalTypes,
    };
  },
  computed: {
    ...mapGetters({
      isSecondRead: RootGetters.IS_SECOND_READ,
      isDirectLink: RootGetters.GET_IS_DIRECT_LINK,
      activeCase: RootGetters.GET_ACTIVE_CASE,
      isCompleted: RootGetters.GET_IS_CASE_COMPLETE,
      isFirstRead: RootGetters.IS_FIRST_READ,
      activeSlide: RootGetters.ACTIVE_SLIDE,
      isTechnical: RootGetters.IS_CASE_HAS_TECHNICAL_ISSUES,
      routeParams: RootGetters.GET_ROUTE_PARAMS,
      labelsConfig: RootGetters.LABELS_CONFIG,
    }),
    columns: function () {
      const secondReadColumns = [
        {
          field: "workflowState",
          title: " ",
          width: "40px",
          cell: "complete",
          sortable: false,
        },
        { field: "caseId", title: this.t("cases_list.case_id") },
        { field: "date", title: this.t("cases_list.date"), filter: "date" },
        {
          field: "tissues",
          title: this.t("cases_list.tissue"),
          type: "string",
          cell: "tissue",
        },
        {
          field: "diagnosis",
          title: this.t("cases_list.diagnosis"),
          type: "string",
          cell: "diagnosis",
        },
        {
          field: "numberOfSlides",
          title: this.t("cases_list.number_of_slides"),
          filter: "numeric",
        },
        {
          field: "alerts",
          title: this.t("cases_list.alerts"),
          filter: "numeric",
          cell: "alerts",
          sortable: false,
        },
        { field: "assignedTo", title: this.t("cases_list.assigned_to"), cell: "assignTo" },
      ];
      const secondReadMininalColumns = [
        {
          field: "workflowState",
          title: " ",
          width: "40px",
          cell: "complete",
          sortable: false,
        },
        { field: "caseId", title: this.t("cases_list.case_id") },
        {
          field: "tissue",
          title: this.t("cases_list.tissue"),
          type: "string",
          cell: "tissue",
        },
        {
          field: "diagnosis",
          title: this.t("cases_list.diagnosis"),
          type: "string",
          cell: "diagnosis",
        },
        {
          field: "alerts",
          title: this.t("cases_list.alerts"),
          filter: "numeric",
          cell: "alerts",
          sortable: false,
        },
      ];
      const firstReadColumns = [
        {
          field: "workflowState",
          title: " ",
          width: "40px",
          cell: "complete",
          sortable: false,
        },
        {
          field: "technicalState",
          title: " ",
          width: "41px",
          cell: "technical",
          sortable: false,
        },
        {
          field: "caseId",
          title: this.t("cases_list.case_id"),
          minWidth: "250px",
        },
        { field: "date", title: this.t("cases_list.date"), filter: "date", minWidth: "150px" },
        {
          field: "tissues",
          title: this.t("cases_list.tissue"),
          type: "string",
          filter: "string",
        },
        {
          field: "numberOfSlides",
          title: this.t("cases_list.number_of_slides"),
          filter: "numeric",
          width: "150px",
        },
        {
          field: "caseLabels",
          title: this.t("cases_list.findings"),
          cell: "caseLabels",
        },
        {
          field: "assignedTo",
          title: this.t("cases_list.assigned_to"),
          cell: "assignTo",
          width: "200px",
        },
      ];
      const firstReadMinimalColumns = [
        {
          field: "workflowState",
          title: " ",
          width: "40px",
          cell: "complete",
          sortable: false,
        },
        {
          field: "technicalState",
          title: " ",
          width: "41px",
          cell: "technical",
          sortable: false,
        },
        { field: "caseId", title: this.t("cases_list.case_id") },
        {
          field: "tissues",
          title: this.t("cases_list.tissue"),
          type: "string",
          filter: "string",
        },
        {
          field: "caseLabels",
          title: this.t("cases_list.findings"),
          cell: "caseLabels",
        },
      ];
      if (this.isSecondRead) {
        return this.selectedID ? secondReadMininalColumns : secondReadColumns;
      } else {
        return this.selectedID ? firstReadMinimalColumns : firstReadColumns;
      }
    },
    result: {
      get: function () {
        const vm = this;
        let filtered = filterBy(this.$store.state.cases, this.filter).map((item: any) => ({
          ...item,
          selected: item.caseId === vm.selectedID,
        }));
        let ordered: any;
        /** Create a custom sort function for caseLabels column.
         * Logic is defined here:
         * https://ibex-ai-squad.monday.com/boards/1204524573/pulses/2990711079
         *
         * The algorithm will set each case with scores according to its labels.
         * Likelihood will count as the most significant for the scores
         * (Each High-Likelihood label will provide 100,000 points,
         *  Each Medium-Likelihood label will provide 10,000 points and
         *  Each Low-likelihood label will provide 1,000 points).
         *
         * Label weights (configured in `insights-config.json`) will provide less
         * points, and each weight has a different amount of points.
         * For example, Cancer has more points than DCIS, but High Likelihood DCIS
         * will give more more points than Medium Likelihood Cancer.
         * For example:
         * High-Likelihood Cancer (weight: 3) will result in a score of 3 * 100,000 / 100 => 300
         * Medium-Likelihood Cancer will result in a score of 3 * 10,000 / 100 => 30
         * High-Likelihood DCIS (weight: 1) will result in 1 * 100,000 / 100 => 100
         * This way, we can assure that althogh DCIS is less significant than Cancer,
         * the likelihood will play the major part in scoring the labels.
         *  */

        if (this.sort[0]?.field === "caseLabels") {
          const likelihoodMap = {
            [Likelihood.LOW]: 10,
            [Likelihood.MEDIUM]: 100,
            [Likelihood.HIGH]: 1000,
          };
          ordered = filtered.sort((a: ICase, b: ICase) => {
            const calcLabelScore = (currentCase: ICase) => {
              // Iterate and sum all scores of each label in given case (currentCase)
              return currentCase.caseLabels.labels.reduce((prevScore: any, label: any) => {
                let score: number = 0;
                let weight: number = 0;
                let arr: Array<string> = [];
                // Add the likelihood score for the given label
                score += likelihoodMap[label.likelihood];

                // Create a new data sctructure that will hold the name of the label
                // and the weight as provided in the insights config
                arr = Object.entries(vm.labelsConfig[currentCase.tissue])
                  // create an array of objects, containing the label
                  // key from the config, and the weight from the config
                  .reduce((result: Array<any>, labelConfigObjEntry: any) => {
                    return result.concat({
                      key: labelConfigObjEntry[0],
                      weight: labelConfigObjEntry[1].weight,
                    });
                  }, [])
                  // sort the result array with objects' weight
                  .sort((a, b) => {
                    return a.weight - b.weight;
                    // modify the result into an array of strings, containing
                    // only the labels, after being sorted by weight (high-last)
                  })
                  .map((insight) => {
                    return insight.key;
                  });
                // calculate the label score per weight
                weight = arr.indexOf(label.key.toLowerCase());
                weight *= likelihoodMap[label.likelihood];
                return prevScore + score + weight;
              }, 0);
            };
            let labelsScoreA = 0;
            let labelsScoreB = 0;
            if (a.caseLabels) {
              labelsScoreA = calcLabelScore(a);
            }
            if (b.caseLabels) {
              labelsScoreB = calcLabelScore(b);
            }
            return vm.sort[0].dir === "desc"
              ? labelsScoreA - labelsScoreB
              : labelsScoreB - labelsScoreA;
          });
        } else {
          ordered = orderBy(filtered, this.sort);
        }
        let incomplete = ordered.filter(
          (item: ICase) => item.workflowState !== WorkflowState.COMPLETE
        );
        incomplete = incomplete.sort(
          (x: ICase, y: ICase) => Number(y.isDiagnosed) - Number(x.isDiagnosed)
        );
        const complete = ordered.filter(
          (item: ICase) => item.workflowState === WorkflowState.COMPLETE
        );
        return [...incomplete, ...complete].slice(this.skip, this.take + this.skip);
      },
      set: function () {},
    },
    total() {
      return filterBy(this.$store.state.cases, this.filter).length;
    },
    selectedItem() {
      return this.$store.state.cases.find((item: ICase) => item.caseId === this.selectedID);
    },
    state() {
      return {
        cases: this.$store.state.cases as ICase[],
        config: this.$store.state.config as any,
        globals: this.$store.state.globals as any,
      };
    },
  },
  async created() {
    if (this.isFirstRead) {
      await this.constructCasesData();
      this.handleContextualLink();
      if (this.$store.state.config["remote_server_enabled"]) {
        setInterval(this.constructCasesData, this.$store.state.config["data_refresh_ms"]);
      }
    } else {
      await this.fetchSrCases();
      this.handleContextualLink();
      if (this.$store.state.config["remote_server_enabled"]) {
        setInterval(this.fetchSrCases, this.$store.state.config["data_refresh_ms"]);
      }
    }
  },
  mounted() {
    this.dataItems = this.$store.state.cases;
    EventBus.on("update-cases", this.updateCases);
    EventBus.on("update-case", this.updateCase);
    EventBus.on("resolution-updated", this.resolutionUpdated);
    EventBus.on("comment-updated", this.commentUpdated);
    EventBus.on("show_cases_view", this.showCasesView);
    EventBus.on("show_case_view", this.showCaseView);
  },
  methods: {
    ...mapActions({
      setActiveCase: RootActions.SET_ACTIVE_CASE,
      setError: RootActions.SET_ERROR,
      fetchSrCases: RootActions.FETCH_SR_CASES,
      constructCasesData: RootActions.CONSTRUCT_CASES_DATA,
    }),
    handleContextualLink() {
      if (this.$store.state.queryParams.get("case") && !this.selectedID) {
        this.case = this.getCaseById(this.$store.state.queryParams.get("case"));
      } else if (this.routeParams && this.routeParams.name === "cl") {
        if (this.routeParams.params.slug.length) {
          try {
            const data = JSON.parse(atob(this.routeParams.params.slug));
            this.case = this.getCaseById(data.case);
          } catch (e) {
            console.error("IBEX Error parsing contextual link:", e);
            this.errorModal = true;
            return;
          }
        }
      }
      if (this.case) {
        this.selectedID = this.case.caseId;
        this.setActiveCase({ id: this.case.caseId });
        this.fetchCase(this.case);
      } else if (this.isDirectLink) {
        this.errorModal = true;
      }
    },
    getCaseById(caseId: string) {
      return this.$store.state.cases.find((caseObj: ICase) => caseObj.caseId === caseId);
    },
    pageChangeHandler: function (event) {
      this.skip = event.page.skip;
      this.take = event.page.take;
    },
    sortChangeHandler: function (e) {
      this.sort = e.sort;
    },
    filterChange: function (e) {
      this.filter = e.filter;
    },
    async onCellClick(event) {
      this.case = event.dataItem;
      this.selectedID = this.case.caseId;
      await this.fetchCase(this.case);
      this.setActiveCase({ id: this.case.caseId });
    },
    fetchCase(caseObj, isUpdate = false) {
      this.selectedID = caseObj.caseId;
      fetch(`${BFF_BASE_PATH}/cases/${encodeURIComponent(this.selectedID)}/slides`)
        .then((response) => {
          if (!response.ok) {
            throw Error(response.statusText);
          }
          return response.json();
        })

        .then(({ slides }) => {
          if (this.selectedID !== caseObj.caseId) return;
          // solution from here: https://stackoverflow.com/questions/40201533/sort-version-dotted-number-strings-in-javascript
          let orderedSlides = slides.sort((a, b) =>
            a.logicalName
              .replace(/\d+/g, (n) => +n + 100000)
              .localeCompare(b.logicalName.replace(/\d+/g, (n) => +n + 100000))
          );
          // make sure we don't overwrite the current slide's insights and casesReports
          orderedSlides = orderedSlides.map((slide: ISlide) => {
            return slide.slideId !== this.activeSlide?.slideId ? slide : this.activeSlide;
          });
          this.$store.commit("slides", orderedSlides);
          if (isUpdate) {
            // means silent re-fetch of case's slides
            EventBus.emit("event-bridge-api", {
              eventName: "case-updated-from-vue",
              data: { case: caseObj, slides: orderedSlides },
            });
          } else {
            // case was just selected from list
            this.$store.commit("removeCurrentSlide");
            this.$store.commit("clearAllHeatmaps");
            EventBus.emit("event-bridge-api", {
              eventName: "case-selected-from-vue",
              data: { case: caseObj, slides: orderedSlides },
            });
          }
          if (this.$store.state.config["remote_server_enabled"]) {
            setTimeout(() => {
              if (this.activeCase?.caseId == caseObj?.caseId) {
                this.fetchCase(caseObj, true);
              }
            }, this.$store.state.config["data_refresh_ms"]);
          }
        })
        .catch((error) => this.setError(this.t("cases_list.failed_fetching_a_case")));
    },
    reassign(event) {
      fetch(
        `${BFF_BASE_PATH}/update_case/${encodeURIComponent(
          event.component.$parent.$props.dataItem.caseId
        )}`,
        {
          method: "PUT",
          headers: {
            "Content-type": "application/json; charset=UTF-8", // Indicates the content
          },
          body: JSON.stringify({ assignto: event.value }),
        }
      )
        .then((response) => {
          if (!response.ok) {
            throw Error(response.statusText);
          }
          this.getCaseById(event.component.$parent.$props.dataItem.caseId).assignedTo = event.value;
          this.$forceUpdate();
        })
        .catch((error) => this.setError(this.t("cases_list.failed_fetching_cases")));
    },
    addDiagnosis() {},
    enableDiagnosis(diagnosis, isDiagnosed) {
      return !diagnosis && !isDiagnosed && this.$store.state.config.manual_diagnosis_enabled;
    },
    searchFilter(event) {
      this.filter = {
        logic: "or",
        filters: [
          { field: "caseId", operator: "contains", value: event.target.value },
          { field: "tissues", operator: "contains", value: event.target.value },
          {
            field: "diagnosis",
            operator: "contains",
            value: event.target.value,
          },
          {
            field: "assignedTo",
            operator: "contains",
            value: event.target.value,
          },
        ],
      };
      this.skip = 0;
    },
    // event binding functions
    updateCases() {
      if (this.isFirstRead) {
        this.constructCasesData();
      } else {
        this.fetchSrCases();
      }
    },
    updateCase(data: any) {
      this.fetchCase(this.getCaseById(data.caseId), true);
    },
    resolutionUpdated(data: any) {
      this.$store.commit("case", data.case);
      this.$store.commit(RootMutations.SET_SLIDE, { updatedSlide: data.slide });
    },
    commentUpdated(data: any) {
      this.$store.commit(RootMutations.SET_SLIDE, { updatedSlide: data.slide });
    },
    showCasesView() {
      this.selectedID = "";
      this.$store.commit("removeCurrentSlide");
      if (!this.isDirectLink) {
        this.$store.commit(RootMutations.REMOVE_CURRENT_CASE);
      }
    },
    showCaseView() {
      this.$store.commit("removeCurrentSlide");
    },
  },
  unmounted() {
    // remove event bus listeners
    EventBus.off("update-cases", this.updateCases);
    EventBus.off("update-case", this.updateCase);
    EventBus.off("resolution-updated", this.resolutionUpdated);
    EventBus.off("comment-updated", this.commentUpdated);
    EventBus.off("show_cases_view", this.showCasesView);
    EventBus.off("show_case_view", this.showCaseView);
  },
});
